import { FormGroup, FormBuilder, Validators, FormControl } from '@angular/forms';
import { PanelBarTemplate } from '../../../../../shared/expandable-panelbar/expandable-panelbar.component';
import { PanelbarService } from '../../../../../shared/expandable-panelbar/expandable-panelbar.service';
import { BehaviorSubject } from 'rxjs';
import { CtwrRequestService } from '../../../ctwr.service';
import { Injector } from '@angular/core';
import { RequestDetailService } from '../detail.service';
import { ApprovalStatus } from '../../../workflow.service';
import { ToolingComponent } from '../../tooling-components/tooling.component';
import { UserDelegateTemplate } from '../delegate.template';
import { FormDialogTemplate } from '../../../../../shared/form-dialog/form-dialog.template';
import { DatePipe } from '@angular/common';
import { HttpParams } from '@angular/common/http';

export class PartApprovalPanelbar extends PanelBarTemplate {

    private readonly formBuilder: FormBuilder;
    private readonly panelbarService: PanelbarService;
    private readonly requestService: CtwrRequestService;
    private readonly detailService: RequestDetailService;
    private readonly toolingComponent: ToolingComponent;
    private readonly userDelegateTemplate: UserDelegateTemplate;
    private requestDetail: any;
    private readonly commentDialog: FormDialogTemplate = new FormDialogTemplate();
    private readonly commentDialogTitle: BehaviorSubject<string> = new BehaviorSubject<string>('');

    constructor(
        private readonly injector: Injector,
    ) {
        super();

        this.formBuilder = injector.get(FormBuilder);
        this.panelbarService = injector.get(PanelbarService);
        this.requestService = injector.get(CtwrRequestService);
        this.detailService = injector.get(RequestDetailService);
        this.toolingComponent = injector.get(ToolingComponent);
        this.userDelegateTemplate = new UserDelegateTemplate(this.injector);

        this.title = 'Part Approvals';
        this.id = this.title;
        this.iconStack = false;
        this.initCommentDialog();

        this.detailService.requestDetail
            .subscribe(x => this.requestDetail = x);
    }

    refresh = () => {
        this.loading.next(true);

        this.detailService.refreshRequest();
        this.detailService.refreshPartApprovalMatrix()
            .then(x => this.loading.next(false))
            .catch(x => this.loading.next(false));
    }

    /**
     * For each Part Approval in the dataItem, create an editForm
     * @param dataItem PartApprovalMatrix Dto
     */
    setPartApprovalData(dataItem: any) {
        this.reset();

        if (dataItem.PartApprovals) {
            dataItem.PartApprovals.forEach(pa => {
                if (pa.PartApprovalDetails.length > 0) {
                    this.addPanelBar(this.getPartApprovalPanelBar(pa), pa.IsPrimaryPart);
                }
            })
        }
        else if (dataItem.PartApprovalDetails) {
            dataItem.PartApprovalDetails.forEach(x => {
                if (x.Status !== ApprovalStatus.PENDING) x.PartApprovalStatus = x.Status;
                const _form: FormGroup = this.getEditForm(x, dataItem);
                this.detailService.addTask(dataItem.WFTaskId, _form, this);

                if (x.IsPTE1) {
                    this.forms.unshift(_form);

                } else {
                    this.forms.push(_form);
                }
            });
        }
    }


    /**
    * Initialize and return Part Approval Panelbar instance
    * @param pa - Part Approval
    */
    private getPartApprovalPanelBar(pa: any): PartApprovalPanelbar {
        const pb = new PartApprovalPanelbar(this.injector);
        pb.approvalStatus.next(pa.PartStatus);
        const _partType: string = pa.IsPrimaryPart ? 'Primary Part' : 'Impacted Part';
        const _title: string = _partType + ` : ${pa.PartApprovalTitle}`;
        pb.title = _title;
        pb.iconStack = false;
        pb.id = pa.PartId;
        pb.iconClass = 'fa-wrench secondary-blue';
        pb.setPartApprovalData(pa);
        return pb;
    }


    /**
     * Construct url using form fields
     * Submit save request then call methods to update UI
     * @param dataItem - form value
     * @param form - form
     */
    private handleLibrarySitePartSave(dataItem, form: FormGroup) {
        const params: HttpParams = new HttpParams()
            .set('RequestId', dataItem.RequestId)
            .set('PartId', dataItem.PartId)
            .set('IsPrimary', dataItem.IsPrimary)
            .set('LibraryImpactCommentsPTE', dataItem.LibraryImpactCommentsPTE)
            .set('blnLibraryImpactPart', dataItem.blnLibraryImpactPart)
            .set('IsPTE1', dataItem.IsPTE1);

        const _path = `/LibraryImpactDetail?${params.toString()}`
        form.get('Enabled').setValue(false);
        form.removeControl('IsLibImpact');
        form.addControl('IsLibImpactValue', new FormControl());
        this.updateFormValidation(form);
        this.requestService.save(dataItem, true, _path)
            .then(x => this.refresh())
    }


    /**
     * Construct Dto by combining properties from dataItem and parentEditDataItem
     * @param dataItem - form value
     * @param isLibraryPart - User response to Library Part radio input
     */
    private getMappedParentEditDataItem(dataItem: any, isLibraryPart: boolean) {
        const parentEditDataItem = dataItem.ParentEditDataItem;
        return {
            RequestId: parentEditDataItem.RequestId,
            PartId: parentEditDataItem.PartId,
            IsPrimary: parentEditDataItem.IsPrimaryPart,
            LibraryImpactCommentsPTE: null,
            IsPTE1: dataItem.IsPTE1,
            blnLibraryImpactPart: isLibraryPart,
            WFTaskId: dataItem.WFTaskId
        }
    }


    /**
     * (yes) Prompt user to enter comments
     * (cancel) clear Library Impact control
     * (yes|no) Submit save request
     */
    private readonly onLibrarySiteChange = (control: any, form: FormGroup) => {
        const ctrl = form.get(control.name);
        const _form = this.getMappedParentEditDataItem(form.value, ctrl.value);
        this.handleLibrarySitePartSave(_form, form)
    }


    /**
     * 
     */
    private readonly handleSubmit = (form: FormGroup) => {
        let _status: ApprovalStatus = form.controls.PartApprovalStatus.value;
        if (!_status) _status = ApprovalStatus.WAIVED;
        const _dto: any = {
            Status: _status,
        };

        if (_status === ApprovalStatus.REJECTED) {
            this.commentDialog.save = ({ dataItem }) => {
                _dto.Response = dataItem.Response;
                this.handleWfUpdate(_dto, form);
            }
            this.commentDialog.cancel = () => {
                form.get('PartApprovalStatus').setValue(null);
            }
            this.commentDialogTitle.next('Reject: Enter reason for Reject')
            this.commentDialog.next({});
            this.panelbarService.openDialog(this.commentDialog);
        } else {
            this.handleWfUpdate(_dto, form)
        }
    }


    private handleWfUpdate(data, form) {
        const _path = `/WFTask/UpdateTaskStatus?id=${form.value.WFTaskId}`;
        form.get('Enabled').setValue(false);
        this.updateFormValidation(form);

        this.loading.next(true);
        this.requestService.save(data, false, _path, false).then(() => {
            this.loading.next(false);
            this.refresh();
        })
            .catch(e => {
                this.requestService.handleError(e);
                this.loading.next(false);
            });
    }


    /**
     * Use parameters to construct and return a FormGroup
     * @param dataItem - form data
     * @param parentEditDataItem - workflow task data
     */
    private getEditForm(dataItem: any, parentEditDataItem): FormGroup {
        const form = this.formBuilder.group(PartApprovalEditForm);
        dataItem.IsLibImpact = dataItem.LibImapctDetail.IsLibImpact;
        dataItem.IsLibImpactValue = dataItem.IsLibImpact;
        if (!dataItem.LibImapctDetail.Enabled) {
            form.removeControl('IsLibImpact');
        } else {
            form.removeControl('IsLibImpactValue');
        }
        dataItem.ParentEditDataItem = parentEditDataItem;
        dataItem.ReviewDate = new DatePipe('en-US')
            .transform(dataItem.ReviewDate, 'short');

        form.reset(dataItem);
        this.updateFormValidation(form);
        return form;
    }


    /**
     * 
     * @param editForm 
     * @param isEnabled 
     */
    private updateFormValidation(editForm: FormGroup) {
        const isFormEnabled: boolean = editForm.get('Enabled').value;

        editForm.get('PartApprovalStatus').disable();

        if (isFormEnabled) {
            editForm.get('PartApprovalStatus').enable();
        }

    }


    /**
     * 
     */
    initCommentDialog() {
        const form = this.formBuilder.group({ Response: ['', Validators.compose([Validators.required, Validators.maxLength(255)])] });
        const controls = [{
            selected: true,
            title: '',
            formControls: [{
                type: 'text',
                name: 'Response',
                placeholder: 'Enter a value',
                title: 'Enter a value',
                stretch: true,
            }]
        }]
        this.commentDialog.initilize(this.commentDialogTitle, form, controls);
    }


    /**
     * 
     */
    private readonly isSubmitDisabled = (c, f): boolean => {
        return !f.value.Enabled ||
            c.name === "submitBtn" && !f.valid;
    }


    /**
     * 
     */
    private readonly isToolingItemsDisabled = (c, f): boolean => {
        return !f.value.AllowEditingToolingItems
    }


    private setPartDetail(part: any, requestId: string, panelData: any): Promise<any> {
        return this.toolingComponent.setPartDetail({
            PartNumber: part.PartNumber,
            ProcessName: part.ProcessName,
            ProcessId: part.ProcessId,
            RequestId: requestId,
            PartId: part.Id
        }, false, panelData.IsPTE1);
    }

    private readonly handleToolingComponent = (form: FormGroup) => {
        let _part: any;
        this.toolingComponent.formDialog.save = ({ dataItem }) => {
            this.handleComponentCategorySave(dataItem, form);
        }
        if (form.value.ParentEditDataItem.IsPrimaryPart) {
            _part = this.detailService.primaryPart.value[0]
        } else {
            _part = this.detailService.impactedParts.value
                .find(x => x.Id === form.value.ParentEditDataItem.PartId);
        }
        this.setPartDetail(_part, form.value.ParentEditDataItem.RequestId, form.value)
            .then(x => {
                this.panelbarService.openDialog(this.toolingComponent.formDialog);
                this.toolingComponent.open(false);
            })
    }


    /**
     * 
     */
    private readonly handleComponentCategorySave = (dataItem: any, form: FormGroup) => {
        const RequestId = form.value.ParentEditDataItem.RequestId;
        const PartId = form.value.ParentEditDataItem.PartId;
        const ccData = this.toolingComponent.getFormData();
        if (RequestId && PartId && ccData) {
            this.requestService.save(ccData, false, `/UpdateToolingComponent?requestId=${RequestId}&partId=${PartId}`)
                .then(() => {
                    this.refresh();
                })
                .catch(e => {
                    this.requestService.handleError(e);
                });
        }
    }


    /**
     * 
     */
    private readonly handleUserDelegateSave = (dataItem, form) => {
        this.loading.next(true);
        form.removeControl('IsLibImpact');
        form.get('Enabled').setValue(false);
        this.updateFormValidation(form);
        this.requestService.save({}, false, `/WFTask/Delegate?id=${dataItem.WFTaskId}&assignedTo=${dataItem.Email}`, false)
            .then(() => {
                this.loading.next(false);
                this.refresh();
            })
            .catch(e => {
                this.requestService.handleError(e);
                this.loading.next(false);
            });

    }


    /**
     * 
     */
    private readonly handleDelegateUser = (form: any = null, control: any = null) => {
        if (!form && !control) return this.detailService.isCtwrAdmin;
        this.panelbarService.openDialog(this.userDelegateTemplate);
        this.userDelegateTemplate.refreshUsers('PTE');
        this.userDelegateTemplate.next({ WFTaskId: form.value.WFTaskId, Email: form.value.PTEEmail });
        this.userDelegateTemplate.save = ({ dataItem }) => {
            this.handleUserDelegateSave(dataItem, form);
        }
        return null;
    }


    /**
     * 
     */
    private readonly isDelegateDisabled = (form): boolean => {
        try {
            return this.loading.value ||
                form.value['Status'] !== ApprovalStatus.PENDING ||
                this.requestDetail.RequestStatus === ApprovalStatus.REWORK_REQUESTED ||
                this.requestDetail.RequestStatus === ApprovalStatus.CANCELLED
        } catch (e) {
            return true;
        }
    }

    private readonly isOptionDisabled = (form, isWaived = false): any => {
        try {
            const data = form.value;
            const p = data.ParentEditDataItem;
            if (!data.Enabled || p.IsPrimaryPart && isWaived) {
                return '';
            }
            else {
                return null;
            }
        } catch (e) {
            return false;
        }
    }

    // FORM CONTROLS
    controls = [
        {
            type: 'radio',
            inline: true,
            text: 'Is this a library or common part?',
            title: 'Is this a library or common part?',
            name: 'IsLibImpact',
            change: this.onLibrarySiteChange,
            stretch: true,
            class: 'bottom-border yellow-highlight',
            options: [
                {
                    value: true,
                    label: 'Yes',
                },
                {
                    value: false,
                    label: 'No',
                },
            ]
        },
        {
            type: 'label',
            name: 'Status',
            title: 'Part Status',
        },
        {
            type: 'label',
            name: 'IsLibImpactValue',
            title: 'Library Impact?',
        },
        {
            type: 'radio',
            inline: true,
            text: 'Part Approval Status',
            title: '',
            name: 'PartApprovalStatus',
            options: [
                {
                    value: 'Waived',
                    label: 'Waive',
                    title: 'Part will be not be included or accepted.',
                    disabled: (form) => this.isOptionDisabled(form, true),
                },
                {
                    value: 'Accepted',
                    label: 'Accept',
                    title: 'Part will be included and accepted',
                    disabled: this.isOptionDisabled,
                },
                {
                    value: 'Rejected',
                    label: 'Reject',
                    title: 'Part will be included and not accepted',
                    disabled: this.isOptionDisabled,
                },
            ]
        },
        {
            type: 'user',
            name: 'PTEName',
            title: (f) => f.value.IsPTE1 ? 'PTE1' : 'PTE2',
            emailField: 'PTEEmail',
            delegate: this.handleDelegateUser,
            disabled: this.isDelegateDisabled
        },
        {
            type: 'label',
            name: 'ReviewDate',
            title: 'Review Date',
        },
        {
            type: 'button-group',
            name: 'toolingBtn',
            buttons: [
                {
                    type: 'action',
                    title: 'Tooling Components',
                    text: 'Tooling Components',
                    iconClass: 'fa fa-fw fa-pencil-square-o',
                    name: 'toolingBtn',
                    disabled: this.isToolingItemsDisabled,
                    click: this.handleToolingComponent,
                },
                {
                    type: 'submit',
                    primary: true,
                    title: 'Submit',
                    text: 'Submit',
                    name: 'submitBtn',
                    disabled: this.isSubmitDisabled,
                    click: this.handleSubmit
                },

            ]
        },
    ]


    libraryPartFormControls: any[] = [{
        type: 'textarea',
        title: 'Comments',
        name: 'LibraryImpactCommentsPTE',
        stretch: true,
    }];

    libraryPartFormDialogTabs = [
        {
            title: "Details",
            selected: true,
            formControls: this.libraryPartFormControls
        },
    ]


}


const PartApprovalEditForm = {
    WFTaskId: [],
    PTEEmail: [],
    IsPTE1: [],
    Enabled: [],
    IsAutoAccepted: [],
    AllowEditingToolingItems: [],

    PTEName: [],
    ReviewDate: [],
    Status: [],
    RequestId: [],
    PartApprovalStatus: ['Waived', Validators.required],
    LibImapctDetail: [],
    IsLibImpactValue: [],
    IsLibImpact: [true, Validators.required],

    ParentEditDataItem: [],
    toolingBtn: [],
    submitBtn: [],
}

